import React, { useState, useMemo } from 'react';
import {motion} from 'framer-motion';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {FileText, Users, BarChart3, Stethoscope, Calendar, Eye, Download, Mail, Search, Filter, X, CheckCircle, ChevronLeft, ChevronRight, ClipboardList, Clock, AlertCircle} from 'lucide-react';
import { Label } from '../ui/label';
import ReviewSubmitPage from './ReviewSubmitPage';
import { apiRequest } from '../../config/api';
import { toast } from '../ui/use-toast';
import { pdf } from '@react-pdf/renderer';
import PatientRecordPDF from '../pdf/PatientRecordPDF';

const AdminOverviewTab = ({stats, submissions, patients = [], appointments = [], doctors = [], appointmentsCount = 0, scheduledAppointmentsCount = 0, campSubmissionsCount = 0, attendanceCount = 0, leaveRequestsCount = 0, leaveRequestsPendingCount = 0, leaveRequestsApprovedCount = 0, onNavigate}) => {
  const [viewingSubmission, setViewingSubmission] = useState(null);
  const [submissionData, setSubmissionData] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [viewingPdfUrl, setViewingPdfUrl] = useState(null); // PDF URL for modal display
  
  // Detect mobile device
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'detailed' - default to list for overview
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Reset to first page when view mode or filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [viewMode, searchTerm, statusFilter]);

  const handleView = async (submission) => {
    try {
      // First, check if PDF URL exists in submission data (same as Print button)
      let pdfUrl = submission.pdf_url;
      
      // If not in submission object, fetch full submission data
      if (!pdfUrl) {
        const data = await apiRequest(`get_patient_record_submission.php?submission_id=${submission.id}`);
        if (data.success) {
          pdfUrl = data.pdf_url;
        }
      }
      
      // If PDF URL exists, show PDF in modal (instead of opening new window)
      if (pdfUrl) {
        // Convert relative URL to absolute if needed
        const absoluteUrl = pdfUrl.startsWith('http') 
          ? pdfUrl 
          : `${window.location.origin}${pdfUrl.startsWith('/') ? '' : '/'}${pdfUrl}`;
        
        // Store PDF URL and show modal
        setViewingPdfUrl(absoluteUrl);
        setViewingSubmission(submission);
        return; // Exit early - PDF will be shown in modal
      }
      
      // Fallback: If no PDF URL exists, open modal with ReviewSubmitPage (generate/view data)
      setViewingPdfUrl(null); // Clear PDF URL
      setViewingSubmission(submission);
      setLoadingData(true);
      
      try {
        // Fetch full submission data including medical history, dental examination, and procedures
        const data = await apiRequest(`get_patient_record_submission.php?submission_id=${submission.id}`);
        if (data.success) {
          // Transform procedures data to match expected format
          // API returns procedures as array, but ReviewSubmitPage expects { rows: [...] } or { procedures: [...] }
          const transformedData = {
            ...data,
            procedures: Array.isArray(data.procedures) 
              ? { rows: data.procedures }
              : (data.procedures || null),
            // Include doctor name for display
            doctor_name: data.doctor_name || submission.doctors?.name || null
          };
          setSubmissionData(transformedData);
        } else {
          toast({
            title: 'Error',
            description: 'Failed to load submission details',
            variant: 'destructive'
          });
        }
      } catch (error) {
        toast({
          title: 'Error',
          description: error.message || 'Failed to load submission details',
          variant: 'destructive'
        });
      } finally {
        setLoadingData(false);
      }
    } catch (error) {
      console.error('Error viewing PDF:', error);
      toast({
        title: 'Error',
        description: 'Failed to open PDF',
        variant: 'destructive'
      });
    }
  };

  const handlePrint = async (submission) => {
    try {
      let pdfUrl = submission.pdf_url;
      
      if (!pdfUrl) {
        const data = await apiRequest(`get_patient_record_submission.php?submission_id=${submission.id}`);
        if (data.success) {
          pdfUrl = data.pdf_url;
        }
      }

      if (pdfUrl) {
        window.open(pdfUrl, '_blank');
        console.log('Opening PDF URL:', pdfUrl);
      } else {
        toast({
          title: 'Error',
          description: 'PDF not available for this submission',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error printing:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to open PDF',
        variant: 'destructive'
      });
    }
  };

  const handleEmail = async (submission) => {
    try {
      let pdfUrl = submission.pdf_url;
      
      if (!pdfUrl) {
        const data = await apiRequest(`get_patient_record_submission.php?submission_id=${submission.id}`);
        if (data.success) {
          pdfUrl = data.pdf_url;
        }
      }

      // Send email - backend will use PDF URL from submission record in database
      const emailData = await apiRequest('email_patient_record.php', {
        method: 'POST',
        body: JSON.stringify({ 
          submission_id: submission.id
          // Note: Backend uses PDF URL from submission record (ensures correct PDF per appointment)
        })
      });
      
      if (emailData.success) {
        toast({
          title: 'Success',
          description: 'Patient record sent via email with PDF download link'
        });
      } else {
        // Check if it's a configuration error (Brevo disabled)
        const isConfigError = emailData.message?.includes('disabled') || 
                              emailData.message?.includes('not configured') ||
                              emailData.message?.includes('API key');
        
        toast({
          title: 'Email Service Not Configured',
          description: isConfigError 
            ? 'Email service is not configured. Please configure Brevo in backend/config/brevo.php'
            : (emailData.message || emailData.error || 'Failed to send email'),
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error sending email:', error);
      const errorMessage = error.message || 'Failed to send email';
      const isConfigError = errorMessage.includes('disabled') || 
                           errorMessage.includes('not configured') ||
                           errorMessage.includes('Brevo email service is disabled');
      
      toast({
        title: isConfigError ? 'Email Service Not Configured' : 'Error',
        description: isConfigError
          ? 'Email service is not configured. Please configure Brevo in backend/config/brevo.php'
          : errorMessage,
        variant: 'destructive'
      });
    }
  };

  // Combine submissions and appointments for Recent Activity
  // Only show completed submissions and scheduled appointments (exclude submitted)
  const combinedRecords = useMemo(() => {
    const records = [];
    
    // Add only completed submissions (exclude submitted/in-progress)
    submissions.forEach(sub => {
      const status = (sub.status || '').toLowerCase();
      // Only include completed submissions
      if (status === 'completed') {
        records.push({
          ...sub,
          type: 'submission',
          recordDate: sub.submitted_at || sub.submittedAt,
          patientName: sub.profiles?.name || sub.patientName,
          doctorName: sub.doctors?.name,
          status: sub.status || 'completed'
        });
      }
    });
    
    // Add appointments with type 'appointment' (only scheduled)
    appointments.forEach(apt => {
      const status = (apt.status || '').toLowerCase();
      // Only include scheduled appointments
      if (status === 'scheduled') {
        // Check if appointment was booked by patient
        const isPatientBooked = apt.creator_type === 'patient' || apt.creator_patient_name;
        // Format creator name with "Dr." prefix only if creator is a doctor (NOT a patient)
        const getBookedByText = () => {
          if (!apt.creator_name && !apt.creator_patient_name) return null;
          const creatorName = apt.creator_name || apt.creator_patient_name;
          
          // If it's a patient, don't add "Dr." prefix
          if (isPatientBooked) {
            return creatorName;
          }
          
          // For users, check if they are a doctor
          const isDoctor = apt.creator_type === 'doctor' || 
                          apt.creator_role === 'doctor' ||
                          apt.creator_type === 'chief_doctor' ||
                          apt.creator_role === 'chief_doctor' ||
                          (apt.creator_type === 'user' && (apt.creator_role === 'doctor' || apt.creator_role === 'chief_doctor'));
          
          if (isDoctor) {
            return `Dr. ${creatorName}`;
          }
          
          // For other roles (admin, staff, etc.), don't add "Dr."
          return creatorName;
        };
        const bookedByText = getBookedByText();
        
        records.push({
          ...apt,
          type: 'appointment',
          recordDate: apt.appointment_date || apt.appointmentDate,
          patientName: apt.patient_name || apt.patientName || apt.profiles?.name,
          doctorName: apt.doctor_name || apt.doctors?.name,
          status: apt.status || 'Scheduled',
          bookedBy: bookedByText,
          isPatientBooked: isPatientBooked
        });
      }
    });
    
    // Sort by date (most recent first)
    return records.sort((a, b) => {
      const dateA = new Date(a.recordDate || 0);
      const dateB = new Date(b.recordDate || 0);
      return dateB - dateA;
    });
  }, [submissions, appointments]);

  // Filter combined records
  const filteredRecords = useMemo(() => {
    return combinedRecords.filter(record => {
      // Combined search - search both patient and doctor names
      if (searchTerm.trim()) {
        const searchLower = searchTerm.toLowerCase();
        const patientName = (record.patientName || '').toLowerCase();
        const doctorName = (record.doctorName || '').toLowerCase();
        
        // Match if search term is found in either patient name or doctor name
        if (!patientName.includes(searchLower) && !doctorName.includes(searchLower)) {
          return false;
        }
      }
      
      // Status filter
      if (statusFilter !== 'all') {
        const recordStatus = (record.status || '').toLowerCase();
        const filterStatus = statusFilter.toLowerCase();
        
        // Map status values
        if (filterStatus === 'submitted') {
          if (record.type === 'submission' && recordStatus !== 'completed') return false;
          if (record.type === 'appointment' && recordStatus !== 'submitted') return false;
        } else if (filterStatus === 'completed') {
          if (recordStatus !== 'completed') return false;
        } else if (filterStatus === 'pending') {
          if (recordStatus !== 'pending' && recordStatus !== 'in-progress') return false;
        } else if (filterStatus === 'scheduled') {
          if (record.type !== 'appointment' || recordStatus !== 'scheduled') return false;
        } else {
          if (recordStatus !== filterStatus) return false;
        }
      }
      
      return true;
    });
  }, [combinedRecords, searchTerm, statusFilter]);

  return (
    <div className="space-y-4 sm:space-y-6 md:space-y-8">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3 sm:gap-4 md:gap-6">
        <Card 
          className="glass-effect border-gray-700 cursor-pointer hover:border-cyan-500 transition-colors"
          onClick={() => onNavigate && onNavigate('doctors')}
        >
          <CardContent className="p-6">
            <div className="flex flex-col">
              <Stethoscope className="h-6 w-6 sm:h-8 sm:w-8 text-cyan-400 flex-shrink-0 mb-2" />
              <p className="text-gray-400 text-xs sm:text-sm">Active Doctors</p>
              <p className="text-xl sm:text-2xl font-bold text-white flex items-baseline gap-1">
                <span>{stats.activeDoctors}</span>
                {stats.totalDoctors && (
                  <span className="text-gray-500 text-sm sm:text-base font-normal">
                    ({stats.totalDoctors})
                  </span>
                )}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="glass-effect border-gray-700 cursor-pointer hover:border-green-500 transition-colors"
          onClick={() => onNavigate && onNavigate('patients')}
        >
          <CardContent className="p-6">
            <div className="flex flex-col">
              <Users className="h-6 w-6 sm:h-8 sm:w-8 text-green-400 flex-shrink-0 mb-2" />
              <p className="text-gray-400 text-xs sm:text-sm">Total Patients</p>
              <p className="text-xl sm:text-2xl font-bold text-white">
                {stats.totalPatients || patients.length || 0}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="glass-effect border-gray-700 cursor-pointer hover:border-orange-500 transition-colors"
          onClick={() => onNavigate && onNavigate('appointments')}
        >
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col">
              <Calendar className="h-6 w-6 sm:h-8 sm:w-8 text-orange-400 flex-shrink-0 mb-2" />
              <p className="text-gray-400 text-xs sm:text-sm">Scheduled Appointments</p>
              <p className="text-xl sm:text-2xl font-bold text-white">
                {scheduledAppointmentsCount || 0}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="glass-effect border-gray-700 cursor-pointer hover:border-blue-500 transition-colors"
          onClick={() => onNavigate && onNavigate('submissions')}
        >
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col">
              <FileText className="h-6 w-6 sm:h-8 sm:w-8 text-blue-400 flex-shrink-0 mb-2" />
              <p className="text-gray-400 text-xs sm:text-sm">Appointment History</p>
              <p className="text-xl sm:text-2xl font-bold text-white">{stats.total}</p>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="glass-effect border-gray-700 cursor-pointer hover:border-teal-500 transition-colors"
          onClick={() => onNavigate && onNavigate('camp_submissions')}
        >
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col">
              <ClipboardList className="h-6 w-6 sm:h-8 sm:w-8 text-teal-400 flex-shrink-0 mb-2" />
              <p className="text-gray-400 text-xs sm:text-sm">Camp Submissions</p>
              <p className="text-xl sm:text-2xl font-bold text-white">
                {campSubmissionsCount || 0}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="glass-effect border-gray-700 cursor-pointer hover:border-indigo-500 transition-colors"
          onClick={() => onNavigate && onNavigate('attendance')}
        >
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col">
              <Clock className="h-6 w-6 sm:h-8 sm:w-8 text-indigo-400 flex-shrink-0 mb-2" />
              <p className="text-gray-400 text-xs sm:text-sm">Attendance</p>
              <p className="text-xl sm:text-2xl font-bold text-white">
                {attendanceCount || 0}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="glass-effect border-gray-700 cursor-pointer hover:border-pink-500 transition-colors"
          onClick={() => onNavigate && onNavigate('attendance', 'approve_leave')}
        >
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col">
              <AlertCircle className="h-6 w-6 sm:h-8 sm:w-8 text-pink-400 flex-shrink-0 mb-2" />
              <p className="text-gray-400 text-xs sm:text-sm">Leave Request</p>
              <p className="text-xl sm:text-2xl font-bold text-white">
                {leaveRequestsCount || 0}
              </p>
              <div className="flex gap-2 mt-1">
                <p className="text-green-400 text-xs">
                  A - {leaveRequestsApprovedCount || 0}
                </p>
                <p className="text-yellow-400 text-xs">
                  P - {leaveRequestsPendingCount || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card className="glass-effect border-gray-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-white">Recent Activity</CardTitle>
              <CardDescription className="text-gray-300">
                Latest submissions and appointments
              </CardDescription>
            </div>
            {/* View Toggle */}
            <div className="flex items-center gap-2">
              <Label htmlFor="view-mode-overview" className="text-white text-sm cursor-pointer">
                List View
              </Label>
              <input
                type="checkbox"
                id="view-mode-overview"
                checked={viewMode === 'list'}
                onChange={(e) => setViewMode(e.target.checked ? 'list' : 'detailed')}
                className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500 cursor-pointer"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="mb-4 space-y-3">
            <div className="flex flex-wrap gap-3 items-end">
              {/* Combined Search Box - Filter by Patient or Doctor Name */}
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs text-gray-400 mb-1 block">Filter by Patient or Doctor Name</label>
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search by patient name or doctor name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="bg-gray-800 border-gray-600 text-white pl-8 h-9"
                  />
                </div>
              </div>
              
              {/* Status Filter */}
              <div className="flex-1 min-w-[150px]">
                <label className="text-xs text-gray-400 mb-1 block">Status</label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="bg-gray-800 border-gray-600 text-white h-9">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="submitted">Submitted</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="in-progress">In Progress</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Clear Filters Button */}
              {(searchTerm || statusFilter !== 'all') && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                  }}
                  className="bg-gray-800 border-gray-600 text-white h-9"
                >
                  <X className="h-3 w-3 mr-1" />
                  Clear
                </Button>
              )}
            </div>
          </div>
          
          {filteredRecords.length > 0
            ? viewMode === 'list' ? (
              /* List View with Horizontal Scroll */
              <>
              <div 
                className="w-full overflow-x-auto pb-2"
                style={{ 
                  scrollbarWidth: 'thin', 
                  scrollbarColor: '#4B5563 #1F2937'
                }}
              >
                <div className="space-y-3">
                  {(() => {
                    const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);
                    const startIndex = (currentPage - 1) * itemsPerPage;
                    const endIndex = startIndex + itemsPerPage;
                    const paginatedRecords = filteredRecords.slice(startIndex, endIndex);

                    return paginatedRecords.map(record => {
                    // Handle both submission and appointment records
                    const isSubmission = record.type === 'submission';
                    const submission = isSubmission ? record : null;
                    const appointment = !isSubmission ? record : null;
                    
                    return (
                    <div
                      key={`${record.type}-${record.id}`}
                      className="p-3 bg-gray-800 rounded-lg flex items-center gap-2 sm:gap-3"
                      style={{ minWidth: '1000px', width: 'max-content' }}
                    >
                    {/* All content in one flex row */}
                    <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                      {/* Status Badge - Icon only on mobile portrait */}
                      {record.status === 'completed' || record.status === 'Completed' ? (
                        <span className="md:hidden w-6 h-6 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0">
                          <CheckCircle className="h-3 w-3 text-green-100" />
                        </span>
                      ) : record.status === 'Scheduled' || record.status === 'scheduled' ? (
                        <span className="md:hidden w-6 h-6 rounded-full bg-purple-600 flex items-center justify-center flex-shrink-0">
                          <Calendar className="h-3 w-3 text-purple-100" />
                        </span>
                      ) : null}
                      
                      {/* Status Badge - Text on desktop */}
                      <span
                        className={`hidden md:flex px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 ${
                          record.status === 'completed' || record.status === 'Completed'
                            ? 'bg-green-600 text-green-100' 
                            : record.status === 'Scheduled' || record.status === 'scheduled'
                            ? 'bg-purple-600 text-purple-100'
                            : 'bg-gray-600 text-gray-100'
                        }`}
                      >
                        {record.status === 'completed' || record.status === 'Completed' 
                          ? 'Completed' 
                          : record.status === 'Scheduled' || record.status === 'scheduled'
                          ? 'Scheduled'
                          : record.status}
                      </span>
                      
                      {/* Type Icon */}
                      {isSubmission ? (
                        <FileText className="h-4 w-4 text-blue-400 flex-shrink-0" />
                      ) : (
                        <Calendar className="h-4 w-4 text-orange-400 flex-shrink-0" />
                      )}
                      
                      {/* Patient Name - Always visible */}
                      <p className="text-white font-medium whitespace-nowrap text-sm sm:text-base">
                        {record.patientName || 'Unknown Patient'}
                      </p>
                      
                      {/* Date - Always visible */}
                      {record.recordDate && (
                        <>
                          <span className="text-gray-500">•</span>
                          <p className="text-gray-400 text-xs sm:text-sm whitespace-nowrap">
                            {new Date(record.recordDate).toLocaleDateString()}
                          </p>
                        </>
                      )}
                      
                      {/* Doctor Name - Always visible */}
                      {record.doctorName && (
                        <>
                          <span className="text-gray-500">•</span>
                          <p className="text-gray-400 text-xs sm:text-sm whitespace-nowrap">
                            Dr. {record.doctorName}
                          </p>
                        </>
                      )}
                      
                      {/* Booked By - Only for appointments */}
                      {!isSubmission && record.bookedBy && (
                        <>
                          <span className="text-gray-500">•</span>
                          <p className={`text-xs sm:text-sm whitespace-nowrap ${
                            record.isPatientBooked 
                              ? 'text-blue-300' 
                              : 'text-gray-400'
                          }`}>
                            Booked by: {record.bookedBy}
                          </p>
                        </>
                      )}
                    </div>
                    
                    {/* Action Buttons (only for submissions) */}
                    {isSubmission && (
                      <>
                        <span className="text-gray-500">•</span>
                        <div className="flex gap-1 sm:gap-2 flex-shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleView(submission)}
                          className="bg-blue-600/20 border-blue-500 text-blue-300 hover:bg-blue-600/30 text-xs px-1.5 sm:px-2 py-1 h-7 w-7 sm:w-auto"
                          title="View"
                        >
                          <Eye className="h-3 w-3 sm:mr-1" />
                          <span className="hidden sm:inline">View</span>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handlePrint(submission)}
                          className="bg-green-600/20 border-green-500 text-green-300 hover:bg-green-600/30 text-xs px-1.5 sm:px-2 py-1 h-7 w-7 sm:w-auto"
                          title="Print"
                        >
                          <Download className="h-3 w-3 sm:mr-1" />
                          <span className="hidden sm:inline">Print</span>
                        </Button>
                        {(submission.profiles?.email || submission.patient_email) && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEmail(submission)}
                            className="bg-purple-600/20 border-purple-500 text-purple-300 hover:bg-purple-600/30 text-xs px-1.5 sm:px-2 py-1 h-7 w-7 sm:w-auto"
                            title="Email"
                          >
                            <Mail className="h-3 w-3 sm:mr-1" />
                            <span className="hidden sm:inline">Email</span>
                          </Button>
                        )}
                        </div>
                      </>
                    )}
                    </div>
                    );
                  });
                  })()}
                </div>
              </div>
              
              {/* Pagination Controls for List View */}
              {(() => {
                const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);
                if (filteredRecords.length <= itemsPerPage) return null;
                
                return (
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-gray-700 mt-4">
                    <div className="text-sm text-gray-400">
                      Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredRecords.length)} of {filteredRecords.length} records
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        Previous
                      </Button>
                      <div className="flex items-center space-x-1">
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                          if (
                            page === 1 ||
                            page === totalPages ||
                            (page >= currentPage - 1 && page <= currentPage + 1)
                          ) {
                            return (
                              <Button
                                key={page}
                                variant={currentPage === page ? "default" : "outline"}
                                size="sm"
                                onClick={() => setCurrentPage(page)}
                                className={
                                  currentPage === page
                                    ? "bg-blue-600 text-white hover:bg-blue-700"
                                    : "border-gray-600 text-gray-300 hover:bg-gray-700"
                                }
                              >
                                {page}
                              </Button>
                            );
                          } else if (
                            page === currentPage - 2 ||
                            page === currentPage + 2
                          ) {
                            return (
                              <span key={page} className="text-gray-500 px-2">
                                ...
                              </span>
                            );
                          }
                          return null;
                        })}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage >= totalPages}
                        className="border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                );
              })()}
              </>
            ) : (
              /* Detailed View with Pagination */
              <>
              <div className="space-y-4">
                {(() => {
                  const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);
                  const startIndex = (currentPage - 1) * itemsPerPage;
                  const endIndex = startIndex + itemsPerPage;
                  const paginatedRecords = filteredRecords.slice(startIndex, endIndex);

                  return paginatedRecords.map(record => {
                  const isSubmission = record.type === 'submission';
                  const submission = isSubmission ? record : null;
                  
                  return (
                    <div
                      key={`${record.type}-${record.id}`}
                      className="p-4 bg-gray-800 rounded-lg"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-3">
                        <div>
                          <p className="text-gray-400 text-sm">Patient</p>
                          <p className="text-white font-medium">{record.patientName || 'Unknown Patient'}</p>
                        </div>
                        {record.doctorName && (
                          <div>
                            <p className="text-gray-400 text-sm">Doctor</p>
                            <p className="text-white">Dr. {record.doctorName}</p>
                          </div>
                        )}
                        <div>
                          <p className="text-gray-400 text-sm">Date</p>
                          <p className="text-white">{new Date(record.recordDate).toLocaleDateString()}</p>
                        </div>
                        <div>
                          <p className="text-gray-400 text-sm">Status</p>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            record.status === 'completed' || record.status === 'Completed' ? 'bg-green-600 text-green-100' :
                            record.status === 'Scheduled' || record.status === 'scheduled' ? 'bg-purple-600 text-purple-100' :
                            'bg-gray-600 text-gray-100'
                          }`}>
                            {record.status}
                          </span>
                        </div>
                      </div>
                      
                      {isSubmission && submission && (
                        <div className="mt-3 pt-3 border-t border-gray-700">
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleView(submission)}
                              className="flex-1 bg-blue-600/20 border-blue-500 text-blue-300 hover:bg-blue-600/30 text-xs"
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              View
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handlePrint(submission)}
                              className="flex-1 bg-green-600/20 border-green-500 text-green-300 hover:bg-green-600/30 text-xs"
                            >
                              <Download className="h-3 w-3 mr-1" />
                              Print
                            </Button>
                            {(submission.profiles?.email || submission.patient_email) && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEmail(submission)}
                                className="flex-1 bg-purple-600/20 border-purple-500 text-purple-300 hover:bg-purple-600/30 text-xs"
                              >
                                <Mail className="h-3 w-3 mr-1" />
                                Email
                              </Button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                  });
                  })()}
              </div>
              
              {/* Pagination Controls for Detailed View */}
              {(() => {
                const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);
                if (filteredRecords.length <= itemsPerPage) return null;
                
                return (
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-gray-700 mt-4">
                    <div className="text-sm text-gray-400">
                      Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredRecords.length)} of {filteredRecords.length} records
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        Previous
                      </Button>
                      <div className="flex items-center space-x-1">
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                          if (
                            page === 1 ||
                            page === totalPages ||
                            (page >= currentPage - 1 && page <= currentPage + 1)
                          ) {
                            return (
                              <Button
                                key={page}
                                variant={currentPage === page ? "default" : "outline"}
                                size="sm"
                                onClick={() => setCurrentPage(page)}
                                className={
                                  currentPage === page
                                    ? "bg-blue-600 text-white hover:bg-blue-700"
                                    : "border-gray-600 text-gray-300 hover:bg-gray-700"
                                }
                              >
                                {page}
                              </Button>
                            );
                          } else if (
                            page === currentPage - 2 ||
                            page === currentPage + 2
                          ) {
                            return (
                              <span key={page} className="text-gray-500 px-2">
                                ...
                              </span>
                            );
                          }
                          return null;
                        })}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage >= totalPages}
                        className="border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                );
              })()}
              </>
            )
            : <div className="text-center py-8">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-400">No records found</p>
                <p className="text-gray-500 text-sm mt-2">Try adjusting your filters</p>
              </div>}
        </CardContent>
      </Card>

      {/* View Submission Dialog */}
      <Dialog open={viewingSubmission !== null} onOpenChange={(open) => {
        if (!open) {
          setViewingSubmission(null);
          setViewingPdfUrl(null); // Clear PDF URL when modal closes
        }
      }}>
        <DialogContent className="glass-effect border-gray-700 bg-gray-900/95 backdrop-blur-sm max-w-7xl w-[98vw] max-h-[95vh] p-0 flex flex-col [&>button]:hidden">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-gray-700">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-white text-xl">
                Patient Record and Invoice
                {viewingSubmission?.appointment_reference_id && (
                  <span className="text-gray-400 text-sm ml-2">
                    - {viewingSubmission.appointment_reference_id}
                  </span>
                )}
              </DialogTitle>
              <div className="flex items-center gap-2">
                {viewingPdfUrl && !isMobile && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (viewingPdfUrl) {
                        // Extract filename from URL to maintain consistency with uploaded filename
                        let filename = 'Patient-Record.pdf'; // Default fallback
                        
                        try {
                          // Extract filename from URL path
                          const urlPath = viewingPdfUrl.split('?')[0]; // Remove query parameters
                          const pathParts = urlPath.split('/');
                          const urlFilename = pathParts[pathParts.length - 1];
                          
                          // If filename exists in URL, use it; otherwise use default
                          if (urlFilename && urlFilename.endsWith('.pdf')) {
                            filename = urlFilename;
                          } else {
                            // Fallback: construct from submission data
                            filename = `Patient-Record-${viewingSubmission?.profiles?.name || 'Record'}-${viewingSubmission?.appointment_reference_id || 'N/A'}.pdf`;
                          }
                        } catch (error) {
                          console.error('Error extracting filename from URL:', error);
                          // Fallback: construct from submission data
                          filename = `Patient-Record-${viewingSubmission?.profiles?.name || 'Record'}-${viewingSubmission?.appointment_reference_id || 'N/A'}.pdf`;
                        }
                        
                        const link = document.createElement('a');
                        link.href = viewingPdfUrl;
                        link.download = filename;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        
                        toast({
                          title: 'Download started',
                          description: 'Your PDF is being downloaded',
                        });
                      }
                    }}
                    className="bg-green-600/20 border-green-500 text-green-300 hover:bg-green-600/30"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setViewingSubmission(null);
                    setViewingPdfUrl(null);
                  }}
                  className="bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </DialogHeader>
          
          {/* PDF Viewer - If PDF URL exists, show PDF */}
          {viewingPdfUrl ? (
            isMobile ? (
              // Mobile: Open PDF in new window (better compatibility with Safari/Chrome mobile)
              <div 
                className="flex-1 flex flex-col items-center justify-center p-6"
                style={{ 
                  minHeight: 'calc(95vh - 120px)',
                  height: 'calc(95vh - 120px)'
                }}
              >
                <div className="text-center space-y-4">
                  <FileText className="h-16 w-16 text-blue-400 mx-auto" />
                  <h3 className="text-white text-lg font-semibold">View PDF on Mobile</h3>
                  <p className="text-gray-300 text-sm max-w-md">
                    For the best viewing experience on mobile devices, the PDF will open in a new window where you can view all pages and use native PDF controls.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
                    <Button
                      onClick={() => {
                        window.open(viewingPdfUrl, '_blank');
                      }}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3"
                      size="lg"
                    >
                      <Eye className="h-5 w-5 mr-2" />
                      Open PDF in New Window
                    </Button>
                    <Button
                      onClick={() => {
                        if (viewingPdfUrl) {
                          // Extract filename from URL to maintain consistency with uploaded filename
                          let filename = 'Patient-Record.pdf'; // Default fallback
                          
                          try {
                            // Extract filename from URL path
                            const urlPath = viewingPdfUrl.split('?')[0]; // Remove query parameters
                            const pathParts = urlPath.split('/');
                            const urlFilename = pathParts[pathParts.length - 1];
                            
                            // If filename exists in URL, use it; otherwise use default
                            if (urlFilename && urlFilename.endsWith('.pdf')) {
                              filename = urlFilename;
                            } else {
                              // Fallback: construct from submission data
                              filename = `Patient-Record-${viewingSubmission?.profiles?.name || 'Record'}-${viewingSubmission?.appointment_reference_id || 'N/A'}.pdf`;
                            }
                          } catch (error) {
                            console.error('Error extracting filename from URL:', error);
                            // Fallback: construct from submission data
                            filename = `Patient-Record-${viewingSubmission?.profiles?.name || 'Record'}-${viewingSubmission?.appointment_reference_id || 'N/A'}.pdf`;
                          }
                          
                          const link = document.createElement('a');
                          link.href = viewingPdfUrl;
                          link.download = filename;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          
                          toast({
                            title: 'Download started',
                            description: 'Your PDF is being downloaded',
                          });
                        }
                      }}
                      variant="outline"
                      className="border-green-500 text-green-400 hover:bg-green-500 hover:text-white px-6 py-3"
                      size="lg"
                    >
                      <Download className="h-5 w-5 mr-2" />
                      Download PDF
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              // Desktop: Use embed tag for better performance and native PDF rendering
              <div 
                className="flex-1 overflow-y-auto overflow-x-hidden" 
                style={{ 
                  minHeight: 'calc(95vh - 120px)',
                  maxHeight: 'calc(95vh - 120px)',
                  height: 'calc(95vh - 120px)',
                  WebkitOverflowScrolling: 'touch',
                  position: 'relative'
                }}
              >
                <embed
                  src={`${viewingPdfUrl}#toolbar=0&navpanes=0&scrollbar=1&view=FitH&zoom=page-width`}
                  type="application/pdf"
                  className="w-full"
                  style={{ 
                    minHeight: 'calc(95vh - 120px)',
                    width: '100%',
                    display: 'block',
                    border: 'none'
                  }}
                />
              </div>
            )
          ) : (
            <div className="flex-1 overflow-y-auto px-6 py-4" id="pdf-content-wrapper">
              {loadingData ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
                </div>
              ) : submissionData ? (
                <ReviewSubmitPage
                  patientId={submissionData.patient_id}
                  patientName={submissionData.patient_name}
                  appointmentId={submissionData.appointment_id || viewingSubmission?.appointment_id || null}
                  medicalHistory={submissionData.medical_history}
                  dentalExamination={submissionData.dental_examination}
                  procedures={
                    Array.isArray(submissionData.procedures) 
                      ? { rows: submissionData.procedures }
                      : submissionData.procedures || null
                  }
                  hideDialog={true}
                  onBack={null}
                  onSubmit={null}
                  doctorName={submissionData.doctor_name || viewingSubmission?.doctors?.name || null}
                />
              ) : null}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminOverviewTab;
